/**
 * Phase 5 — useAssistantMutations TanStack Query mutation hooks.
 *
 * Provides create + remove mutations for AI conversations.
 * Both mutations invalidate the ['ai', 'conversations'] queryKey on success
 * so the conversation list re-fetches automatically.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useAssistantMutations() {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: async (): Promise<{ id: string; created_at: string }> => {
      const res = await fetch('/api/ai/conversations', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<{ id: string; created_at: string }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai', 'conversations'] });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`/api/ai/conversations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      // 204 No Content is a success
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai', 'conversations'] });
    },
  });

  return { create, remove };
}
